import { loincCoding } from '#src/models/loinc.ts'
import type { Condition } from '#src/observation-logic/conditionSchema.ts'
import {
  SLUMS_DOMAINS,
  SLUMS_EDUCATION_ANSWERS,
} from '#src/terminology/instruments/instrumentTerminology.ts'
import {
  numberItem,
  ownCoding,
  scoreBand,
  scoredItem,
  totalScore,
} from '#src/terminology/instruments/templateParts.ts'
import type {
  InstrumentSection,
  InstrumentTemplate,
} from '#src/terminology/instruments/types.ts'

const EDUCATION = ownCoding('slums-education', 'SLUMS high school education')

const SECTIONS: InstrumentSection[] = [
  {
    id: 'education',
    items: [
      scoredItem(EDUCATION, SLUMS_EDUCATION_ANSWERS, {
        scaleType: 'Nom',
        label: 'High school education',
        scored: false,
      }),
    ],
  },
  {
    id: 'domains',
    items: SLUMS_DOMAINS.map(([domain, label, max, hint]) =>
      numberItem(
        ownCoding(`slums-${domain}`, `SLUMS ${label.toLowerCase()}`),
        { min: 0, max },
        { label, hint }
      )
    ),
  },
]

function educated(answer: 'yes' | 'no'): Condition {
  return {
    type: 'compare',
    operand: { source: 'observation', code: EDUCATION.code ?? '' },
    op: '=',
    value: { kind: 'coding', code: `slums-education-${answer}` },
  }
}

// The cutoffs by education are the form's scoring table (Tariq SH et al. Am J
// Geriatr Psychiatry 2006;14(11):900-10, doi:10.1097/01.JGP.0000221510.33817.86),
// so no band holds, and no band shows, until education is answered.
export const SLUMS_TEMPLATE: InstrumentTemplate = {
  id: 'slums',
  version: '1',
  panel: loincCoding(
    '71947-6',
    'St. Louis University Mental Status (SLUMS) Examination [SLUMS]'
  ),
  shortName: 'SLUMS',
  title: 'St. Louis University Mental Status (SLUMS) Examination',
  copyright:
    'SLUMS Examination developed by Tariq SH, Tumosa N, Chibnall JT, Perry HM III and Morley JE, Saint Louis University. Questions: aging@slu.edu.',
  reference:
    'https://www.slu.edu/medicine/internal-medicine/geriatric-medicine/aging-successfully/-pdf/slums-form.pdf',
  sections: SECTIONS,
  total: totalScore({
    templateId: 'slums',
    coding: loincCoding('71492-3', 'Total score [SLUMS]'),
    sections: SECTIONS,
    completeness: { requireAll: true },
    bands: [
      scoreBand(
        { min: 27 },
        'slums-normal-high-school',
        'Normal',
        'green',
        educated('yes')
      ),
      scoreBand(
        { min: 21, max: 26 },
        'slums-mild-high-school',
        'Mild neurocognitive disorder',
        'yellow',
        educated('yes')
      ),
      scoreBand(
        { max: 20 },
        'slums-dementia-high-school',
        'Dementia',
        'red',
        educated('yes')
      ),
      scoreBand(
        { min: 25 },
        'slums-normal-less-than-high-school',
        'Normal',
        'green',
        educated('no')
      ),
      scoreBand(
        { min: 20, max: 24 },
        'slums-mild-less-than-high-school',
        'Mild neurocognitive disorder',
        'yellow',
        educated('no')
      ),
      scoreBand(
        { max: 19 },
        'slums-dementia-less-than-high-school',
        'Dementia',
        'red',
        educated('no')
      ),
    ],
  }),
}
